import React, { useMemo } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { getConnectionLabel } from "@common/helpers/financialConnection";
import { getTransactionAccountNameWithLast4 } from "@common/helpers/transactions";
import { getLeadingAccountConnection } from "@common/helpers/financialAccount";
import {
    getIntegrationConnectionInstitutionName,
    isIntegrationConnection,
} from "@common/helpers/integrationConnection";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useIdCopy } from "@hooks/useIdCopy";
import { FinancialAccount } from "@common/types/financialAccount";
import {
    AccountCard,
    AccountCardProps,
} from "@components/financialAccount/AccountCard/AccountCard";

const MAX_NAME_LENGTH = 8;

// --- Inner component: pure rendering, no per-instance query subscription ---

interface InnerProps {
    transaction: TransactionListItem;
    size?: AccountCardProps["iconSize"];
    financialAccounts: FinancialAccount[];
}

export const TransactionAccountColumnContentInner: React.FC<InnerProps> =
    React.memo(
        ({ transaction, size = "sm", financialAccounts }) => {
            const { getCopyProps } = useIdCopy({ label: "Account" });

            const accountsById = useMemo(
                () => new Map(financialAccounts.map((a) => [a.id, a])),
                [financialAccounts],
            );
            const account = accountsById.get(transaction.financialAccount.id)!;
            const transactionAccount = account ?? transaction.financialAccount;
            const connection = getLeadingAccountConnection(transactionAccount);
            const institutionName =
                connection &&
                (isIntegrationConnection(connection)
                    ? getIntegrationConnectionInstitutionName(connection)
                    : getConnectionLabel(connection));
            const accountName = getTransactionAccountNameWithLast4(
                transaction,
                2,
            );
            const tooltipNeeded =
                accountName.length > MAX_NAME_LENGTH ||
                !!transactionAccount.accountNumberMask;

            return (
                <Tooltip
                    disabled={!tooltipNeeded}
                    content={
                        <>
                            {institutionName &&
                                institutionName !== accountName && (
                                    <label>{institutionName}</label>
                                )}
                            <p className="mb-0">{accountName}</p>
                        </>
                    }
                >
                    <div {...getCopyProps(transactionAccount.id)}>
                        <AccountCard
                            account={transactionAccount}
                            onlyNumber
                            showType={false}
                            iconSize={size}
                            maxNameLength={MAX_NAME_LENGTH}
                        />
                    </div>
                </Tooltip>
            );
        },
        (prev, next) =>
            prev.transaction === next.transaction &&
            prev.size === next.size &&
            prev.financialAccounts === next.financialAccounts,
    );
TransactionAccountColumnContentInner.displayName =
    "TransactionAccountColumnContentInner";

// --- Wrapper: calls useFinancialAccounts hook, for standalone use outside table ---

interface Props {
    transaction: TransactionListItem;
    size?: AccountCardProps["iconSize"];
}

export const TransactionAccountColumnContent: React.FC<Props> = React.memo(
    ({ transaction, size = "sm" }) => {
        const financialAccounts = useFinancialAccounts();
        return (
            <TransactionAccountColumnContentInner
                transaction={transaction}
                size={size}
                financialAccounts={financialAccounts}
            />
        );
    },
);
TransactionAccountColumnContent.displayName = "TransactionAccountColumnContent";
